import type { InvoiceDetailResponse, InvoicePart } from '../../../types/invoice';
import { filterOutIgnoredTabCalculationMismatches } from '../../../utils/invoiceIgnoredTabFlags';
import { formatInvoiceDetailMoney } from './invoiceDetailPageUtils';

export type ArithmeticLineIssue = {
    partIndex: number;
    partNumber?: string;
    issueDetails?: {
        invoiceQty?: number;
        invoiceUnitPrice?: number;
        invoiceAmount?: number;
        calculatedAmount?: number;
    };
};

export function buildArithmeticFlagMessages(args: {
    arithmeticLineIssues: ArithmeticLineIssue[];
    lineItems: InvoicePart[];
    freightItems: InvoicePart[];
    fuelItems: InvoicePart[];
    restockingFeeItems: InvoicePart[];
    discountItems: InvoicePart[];
    ignoredParts: InvoicePart[];
    localGrandTotalVsAboveLineItemsWithFeesMismatch: {
        extractedTotal: number;
        expectedFromAboveLineItems: number;
    } | null;
}): string[] {
    const {
        arithmeticLineIssues,
        lineItems,
        freightItems,
        fuelItems,
        restockingFeeItems,
        discountItems,
        ignoredParts,
        localGrandTotalVsAboveLineItemsWithFeesMismatch: grandVsComp,
    } = args;

    const msgs: string[] = [];
    const resolveItemByGlobalIndex = (globalIdx: number) => {
        let o = 0;
        const segs = [lineItems, freightItems, fuelItems, restockingFeeItems, discountItems, ignoredParts];
        for (const seg of segs) {
            if (globalIdx >= o && globalIdx < o + seg.length) return seg[globalIdx - o];
            o += seg.length;
        }
        return null;
    };

    arithmeticLineIssues.forEach((issue) => {
        const idx = issue.partIndex;
        const baseLineIdx = typeof idx === 'number' ? idx : -1;
        const lineNumber = baseLineIdx >= 0 ? baseLineIdx + 1 : null;
        const item = baseLineIdx >= 0 ? resolveItemByGlobalIndex(baseLineIdx) : null;
        const details = issue.issueDetails || {};
        const qty = details.invoiceQty ?? item?.qty;
        const unit = details.invoiceUnitPrice ?? item?.unitBuyPrice;
        const invoiceAmount = details.invoiceAmount ?? item?.totalBuyPrice;
        const calculatedAmount =
            details.calculatedAmount ??
            (typeof qty === 'number' && typeof unit === 'number' ? qty * unit : undefined);

        const label =
            item?.partNumber || item?.title || (lineNumber != null ? `Line ${lineNumber}` : 'Line item');
        const prefix =
            lineNumber != null ? `Line ${lineNumber} (${label})` : `Line item (${label})`;

        const parts: string[] = [];
        if (typeof qty === 'number' && typeof unit === 'number' && typeof calculatedAmount === 'number') {
            parts.push(
                `qty ${qty} × unit price ${formatInvoiceDetailMoney(unit)} = ${formatInvoiceDetailMoney(calculatedAmount)}`,
            );
        }
        if (typeof invoiceAmount === 'number') {
            parts.push(`line total is ${formatInvoiceDetailMoney(invoiceAmount)}`);
        }

        if (parts.length > 0) {
            msgs.push(`${prefix}: ${parts.join(', but ')}.`);
        } else {
            msgs.push(`${prefix}: arithmetic mismatch between qty × unit price and line total.`);
        }
    });

    if (grandVsComp) {
        msgs.push(
            `Total is ${formatInvoiceDetailMoney(grandVsComp.extractedTotal)}, but above line items subtotal + extracted tax + discount + conditional header freight is ${formatInvoiceDetailMoney(grandVsComp.expectedFromAboveLineItems)}.`,
        );
    }

    return msgs;
}

export function computeArithmeticLineIssues(args: {
    localLineArithmeticIssues: ArithmeticLineIssue[];
    invoice: InvoiceDetailResponse | undefined;
    editedLineItems: InvoicePart[] | undefined;
    editedFreightItems: InvoicePart[] | undefined;
    editedFuelItems: InvoicePart[] | undefined;
    editedRestockingFeeItems: InvoicePart[] | undefined;
    editedIgnoredParts: InvoicePart[] | undefined;
}): ArithmeticLineIssue[] {
    const local = args.localLineArithmeticIssues ?? [];
    if (local.length > 0) return local;
    if (!args.invoice?.reconciliationIssues?.flaggedParts?.length) return [];

    const tabs = {
        lineItems: args.editedLineItems ?? args.invoice.lineItems ?? [],
        freightItems: args.editedFreightItems ?? args.invoice.freightItems ?? [],
        fuelItems: args.editedFuelItems ?? args.invoice.fuelItems ?? [],
        restockingFeeItems: args.editedRestockingFeeItems ?? args.invoice.restockingFeeItems ?? [],
        ignoredParts: args.editedIgnoredParts ?? args.invoice.ignoredParts ?? [],
    };

    const visible = filterOutIgnoredTabCalculationMismatches(
        args.invoice.reconciliationIssues.flaggedParts,
        tabs,
    );
    return visible.filter((i: { issueType: string }) => i.issueType === 'calculation_mismatch') as ArithmeticLineIssue[];
}
